import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StatusBadge from '../StatusBadge/StatusBadge';
import AnonymizedBadge from '../../../patient/components/AnonymizedBadge';
import { formatPhone } from '../../../../utils/phoneUtils';
import DemoTag from '../../../../components/demo-data/DemoTag';
import { isDemoRecord } from '../../../../components/demo-data/demoData';
import { getAgeInMonths } from '../../../../utils/patientAvatarUtils';

// Avatar color palette based on name hash
const AVATAR_COLORS = [
  { bg: '#E3F2FD', color: '#1565C0' },
  { bg: '#F3E5F5', color: '#6A1B9A' },
  { bg: '#E8F5E9', color: '#2E7D32' },
  { bg: '#FFF3E0', color: '#E65100' },
  { bg: '#FCE4EC', color: '#880E4F' },
  { bg: '#E0F7FA', color: '#006064' },
  { bg: '#FFF8E1', color: '#F57F17' },
  { bg: '#EDE7F6', color: '#4527A0' },
];

/**
 * Get avatar color based on name hash
 */
function getAvatarColor(name = '') {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

/**
 * Get initials from full name (first + last word)
 */
function getInitials(name = '') {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/**
 * Idade para a lista: anos a partir de 1 ano; abaixo disso em meses (como a
 * agenda) e, no primeiro mês, em dias.
 */
function formatAgeLabel(birthday, date, today, years) {
  if (years >= 1) return `(${years} ${years === 1 ? 'ano' : 'anos'})`;
  const months = getAgeInMonths(birthday);
  if (months === null) return '';
  if (months >= 1) return `(${months} ${months === 1 ? 'mês' : 'meses'})`;
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.max(0, Math.round((startOfToday - date) / (24 * 60 * 60 * 1000)));
  return `(${days} ${days === 1 ? 'dia' : 'dias'})`;
}

/**
 * Format birthday and calculate age
 */
function formatBirthday(birthday = '') {
  if (!birthday) return { date: '—', age: '' };
  try {
    const [year, month, day] = birthday.split('-');
    if (!year || !month || !day) return { date: '—', age: '' };
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    const today = new Date();
    let age = today.getFullYear() - date.getFullYear();
    const monthDiff = today.getMonth() - date.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
      age--;
    }
    return {
      date: `${day}/${month}/${year}`,
      age: formatAgeLabel(birthday, date, today, age),
    };
  } catch {
    return { date: '—', age: '' };
  }
}

/**
 * Highlight search term in text
 */
function HighlightText({ text = '', highlight = '' }) {
  if (!highlight || !text) return <span>{text}</span>;
  const regex = new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return (
    <span>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark
            key={i}
            style={{
              backgroundColor: '#FFF176',
              color: 'inherit',
              borderRadius: '2px',
              padding: '0 1px',
            }}
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
}

/**
 * PatientRow - Single row in the patients table
 * @param {Object} props
 * @param {Object} props.patient - Patient data object
 * @param {Function} props.onView - Callback to view patient profile
 * @param {Function} props.onSchedule - Callback to schedule appointment
 * @param {Function} props.onEdit - Callback to edit patient
 * @param {string} props.highlight - Search term to highlight in name
 */
function PatientRow({ patient, onView, onSchedule, onEdit, highlight, isMobile }) {
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState(null);

  const { id, name, cpf, status, phone, email, birthday, anonymized_at, deleted_at } = patient;
  const avatarColor = getAvatarColor(name);
  const initials = getInitials(name);
  const formattedPhone = formatPhone(phone);
  const { date: birthdayDate, age: birthdayAge } = formatBirthday(birthday);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleView = (e) => {
    e.stopPropagation();
    if (onView) onView(id);
    else navigate(`/patients/${id}`);
  };

  const handleSchedule = (e) => {
    e.stopPropagation();
    if (onSchedule) onSchedule(id);
  };

  const handleEdit = () => {
    handleMenuClose();
    if (onEdit) onEdit(id);
  };

  const handleMenuView = () => {
    handleMenuClose();
    if (onView) onView(id);
    else navigate(`/patients/${id}`);
  };

  const handleMenuSchedule = () => {
    handleMenuClose();
    if (onSchedule) onSchedule(id);
  };

  return (
    <TableRow
      hover
      sx={{
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
      }}
      onClick={handleView}
    >
      {/* Patient: Avatar + Name + CPF */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: avatarColor.bg,
              color: avatarColor.color,
              fontSize: '0.8125rem',
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {initials}
          </Avatar>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}
              >
                <HighlightText text={name} highlight={highlight} />
              </Typography>
              {isDemoRecord(patient) && <DemoTag />}
            </Box>
            {(anonymized_at || deleted_at) && (
              <Box sx={{ mt: 0.3 }}>
                {deleted_at
                  ? <AnonymizedBadge anonymizedAt={deleted_at} label="Excluído em" compact />
                  : <AnonymizedBadge anonymizedAt={anonymized_at} compact />}
              </Box>
            )}
            {cpf && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {cpf}
              </Typography>
            )}
          </Box>
        </Box>
      </TableCell>

      {/* Contact: Phone + Email */}
      <TableCell>
        <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.4 }}>
          {formattedPhone || '—'}
        </Typography>
        {email && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {email}
          </Typography>
        )}
      </TableCell>

      {/* Birthday + Age — ocultar em mobile (<760px) */}
      {!isMobile && (
        <TableCell>
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {birthdayDate}
          </Typography>
          {birthdayAge && (
            <Typography variant="caption" color="text.secondary">
              {birthdayAge}
            </Typography>
          )}
        </TableCell>
      )}

      {/* Status — ocultar em mobile (<760px) */}
      {!isMobile && (
        <TableCell align="left" sx={{ width: 100 }}>
          <StatusBadge status={status} />
        </TableCell>
      )}

      {/* Actions */}
      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
          {!isMobile && (
            <>
              <Tooltip title="Ver ficha">
                <IconButton size="small" onClick={handleView} sx={{ color: 'text.secondary' }}>
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Agendar consulta">
                <IconButton size="small" onClick={handleSchedule} sx={{ color: 'text.secondary' }}>
                  <CalendarTodayOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Tooltip title="Mais opções">
            <IconButton size="small" onClick={handleMenuOpen} sx={{ color: 'text.secondary' }}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: { borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 160 },
          }}
        >
          <MenuItem onClick={handleEdit} sx={{ fontSize: '0.875rem' }}>
            Editar
          </MenuItem>
          <MenuItem onClick={handleMenuView} sx={{ fontSize: '0.875rem' }}>
            Ver ficha
          </MenuItem>
          <MenuItem onClick={handleMenuSchedule} sx={{ fontSize: '0.875rem' }}>
            Agendar
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
}

export default PatientRow;
